import React from 'react';
import { Trash2 } from 'lucide-react';
import { WorkType } from 'types/project/projectDefaults';
import { ProjectUser } from 'types/project';
import { User } from 'types/task';
import TaskTypeDropdown from '../Dropdowns/TaskTypeDropdown';
import UserDropdown from '../Dropdowns/UserDropdown';

interface ChildItemsCreateFormProps {
  inputValue: string;
  onInputChange: (val: string) => void;
  draftTypeUuid: string | null;
  setDraftTypeUuid: (uuid: string | null) => void;
  draftAssigneeUuid: string | null;
  setDraftAssigneeUuid: (uuid: string | null) => void;
  allowedChildTypes: WorkType[];
  availableUsers: ProjectUser[];
  isSubtaskMode: boolean;
  isAdding: boolean;
  disabled: boolean;
  onAdd: () => void;
  onCancel: () => void;
}

const ChildItemsCreateForm: React.FC<ChildItemsCreateFormProps> = ({
  inputValue,
  onInputChange,
  draftTypeUuid,
  setDraftTypeUuid,
  draftAssigneeUuid,
  setDraftAssigneeUuid,
  allowedChildTypes,
  availableUsers,
  isSubtaskMode,
  isAdding,
  disabled,
  onAdd,
  onCancel,
}) => (
  <div className="flex flex-wrap items-start gap-3 w-full relative z-[50] mb-4 transition-all animate-in fade-in slide-in-from-top-2 duration-200">
    {/* Title input with inline type + assignee controls */}
    <div className="flex-1 min-w-[200px] relative">
      <div className="bg-primary-variant border border-primary-subtle rounded-lg px-3 py-1 flex items-center min-h-[42px] relative z-20 w-full focus-within:border-purple-500/50 transition-colors">
        <input
          autoFocus
          value={inputValue}
          onChange={e => onInputChange(e.target.value)}
          disabled={disabled}
          maxLength={255}
          placeholder={isSubtaskMode ? 'Subtask name...' : 'Type child name...'}
          className="flex-1 bg-transparent outline-none text-sm text-neutral placeholder:text-neutral-medium disabled:opacity-50 min-w-[120px]"
          onKeyDown={e => {
            if (e.key === 'Enter' && inputValue.trim()) {
              e.preventDefault();
              onAdd();
            }
          }}
        />
        <div className="flex items-center gap-1 border-l border-primary-subtle pl-2 ml-2 shrink-0 h-8">
          <div className="shrink-0">
            <TaskTypeDropdown
              workTypes={allowedChildTypes}
              currentTypeUuid={draftTypeUuid || (allowedChildTypes[0]?.workTypeUuid ?? '')}
              onTypeChange={(wt: WorkType) => setDraftTypeUuid(wt.workTypeUuid)}
              size="sm"
              disabled={disabled || allowedChildTypes.length === 0}
            />
          </div>
          <div className="shrink-0 flex justify-center">
            <UserDropdown
              users={availableUsers}
              currentUserUuid={draftAssigneeUuid}
              onUserChange={u => setDraftAssigneeUuid(u?.appUserUuid || null)}
              size="sm"
              showName={false}
              placeholder=""
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </div>

    {/* Add button */}
    <button
      onClick={onAdd}
      disabled={disabled || !inputValue.trim() || isAdding}
      className="bg-primary-subtle hover:bg-primary-intermediate text-neutral-light text-sm font-semibold px-5 min-h-[42px] rounded-lg transition-colors disabled:opacity-50 shrink-0 flex items-center justify-center gap-2 min-w-[80px]"
    >
      {isAdding ? (
        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ) : 'Add'}
    </button>

    {/* Cancel button */}
    <button
      onClick={onCancel}
      disabled={disabled}
      className="bg-primary-subtle text-neutral-medium hover:text-red-400 text-sm px-3.5 h-[42px] rounded-lg transition-colors disabled:opacity-50 shrink-0 flex items-center justify-center"
    >
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

export default ChildItemsCreateForm;
